import { format, isValid, parseISO, parse } from 'date-fns';

import { normalizeUsStateCode } from './usStateNormalize';

// Date format constants using date-fns format patterns
export const FULL_FORMAT = "MMMM d, yyyy 'at' h:mm a";
export const DATE_ONLY_FORMAT = 'MMMM d, yyyy';
export const TIME_ONLY_FORMAT = 'h:mm a';
export const SHORT_DATE_FORMAT = 'MMM d, yyyy';
export const LONG_DATE_FORMAT = "MMMM d, yyyy 'at' h:mm:ss a";
export const ISO_DATE_FORMAT = 'yyyy-MM-dd';
export const DISPLAY_DATE_FORMAT = 'MMM dd, yyyy';
export const ISO_TIME_FORMAT = 'HH:mm';

const STATE_TO_TIMEZONE: Record<string, string> = {
  CA: 'America/Los_Angeles',
  WA: 'America/Los_Angeles',
  OR: 'America/Los_Angeles',
  NV: 'America/Los_Angeles',
  MT: 'America/Denver',
  ID: 'America/Denver',
  WY: 'America/Denver',
  UT: 'America/Denver',
  CO: 'America/Denver',
  NM: 'America/Denver',
  AZ: 'America/Phoenix',
  ND: 'America/Chicago',
  SD: 'America/Chicago',
  TX: 'America/Chicago',
  OK: 'America/Chicago',
  KS: 'America/Chicago',
  NE: 'America/Chicago',
  IA: 'America/Chicago',
  MO: 'America/Chicago',
  AR: 'America/Chicago',
  LA: 'America/Chicago',
  MN: 'America/Chicago',
  WI: 'America/Chicago',
  IL: 'America/Chicago',
  MI: 'America/New_York',
  IN: 'America/New_York',
  KY: 'America/New_York',
  TN: 'America/Chicago',
  MS: 'America/Chicago',
  AL: 'America/Chicago',
  OH: 'America/New_York',
  NY: 'America/New_York',
  PA: 'America/New_York',
  NJ: 'America/New_York',
  DE: 'America/New_York',
  MD: 'America/New_York',
  DC: 'America/New_York',
  VA: 'America/New_York',
  WV: 'America/New_York',
  NC: 'America/New_York',
  SC: 'America/New_York',
  GA: 'America/New_York',
  FL: 'America/New_York',
  ME: 'America/New_York',
  NH: 'America/New_York',
  VT: 'America/New_York',
  MA: 'America/New_York',
  RI: 'America/New_York',
  CT: 'America/New_York',
  AK: 'America/Anchorage',
  HI: 'Pacific/Honolulu',
};

type FormatType =
  | 'FULL_FORMAT'
  | 'DATE_ONLY_FORMAT'
  | 'TIME_ONLY_FORMAT'
  | 'SHORT_DATE_FORMAT'
  | 'LONG_DATE_FORMAT'
  | 'ISO_DATE_FORMAT'
  | 'DISPLAY_DATE_FORMAT'
  | 'ISO_TIME_FORMAT';

// Format mapping for backward compatibility
const formatMap: Record<FormatType, string> = {
  FULL_FORMAT,
  DATE_ONLY_FORMAT,
  TIME_ONLY_FORMAT,
  SHORT_DATE_FORMAT,
  LONG_DATE_FORMAT,
  ISO_DATE_FORMAT,
  DISPLAY_DATE_FORMAT,
  ISO_TIME_FORMAT,
};

const BOOKING_TIMEZONE_FALLBACK = 'America/Los_Angeles';

export const getTimezoneFromState = (
  state: string | null | undefined
): string => {
  if (!state?.trim()) {
    return BOOKING_TIMEZONE_FALLBACK;
  }
  const code = normalizeUsStateCode(state.trim());
  return STATE_TO_TIMEZONE[code] ?? BOOKING_TIMEZONE_FALLBACK;
};

const toBookingTimezoneDate = (
  instant: Date,
  timezone: string
): Date | null => {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parts = dtf.formatToParts(instant);
    const year = Number(parts.find(p => p.type === 'year')?.value);
    const month = Number(parts.find(p => p.type === 'month')?.value);
    const day = Number(parts.find(p => p.type === 'day')?.value);
    const hour = Number(parts.find(p => p.type === 'hour')?.value);
    const minute = Number(parts.find(p => p.type === 'minute')?.value);
    const second = Number(parts.find(p => p.type === 'second')?.value);

    if ([year, month, day, hour, minute, second].some(n => Number.isNaN(n))) {
      return null;
    }

    // Build a stable Date carrying wall-clock parts in local runtime timezone.
    return new Date(year, month - 1, day, hour, minute, second, 0);
  } catch {
    return null;
  }
};

/**
 * Formats a date string using date-fns with predefined format patterns
 * @param dateString - The date string to format
 * @param formatType - The format type to use
 * @returns Formatted date string
 */
export const dateFormat = (
  dateString: string,
  formatType: FormatType
): string => {
  try {
    // Parse the date string - handle both ISO strings and other formats
    const date = parseISO(dateString);

    // Check if date is valid
    if (!isValid(date)) {
      throw new Error('Invalid date string');
    }

    const formatPattern = formatMap[formatType];
    return format(date, formatPattern);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Formats a date using a custom date-fns format pattern
 * @param dateString - The date string to format
 * @param customFormat - Custom date-fns format pattern
 * @returns Formatted date string
 */
export const dateFormatCustom = (
  dateString: string,
  customFormat: string
): string => {
  try {
    const date = parseISO(dateString);

    if (!isValid(date)) {
      throw new Error('Invalid date string');
    }

    return format(date, customFormat);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Parses API booking fields into a Date (browser-local when formatted).
 * Matches customer mobile `convertUTCBookingToLocal`: prefer `booking_date_time` (UTC ISO),
 * else combine `booking_date` + `booking_time` as UTC.
 */
export const parseBookingUtcInstant = (
  bookingDate: string | null | undefined,
  bookingTime: string | null | undefined,
  bookingDateTime?: string | null | undefined
): Date | null => {
  if (bookingDateTime?.trim()) {
    try {
      const d = parseISO(bookingDateTime.trim());
      if (isValid(d)) {
        return d;
      }
    } catch {
      // fall through
    }
  }
  if (!bookingDate?.trim() || !bookingTime?.trim()) {
    return null;
  }
  const normalizedTime = (() => {
    const t = bookingTime.trim();
    const parts = t.split(':');
    if (parts.length === 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`;
    }
    return t;
  })();
  const d = parseISO(`${bookingDate.trim()}T${normalizedTime}Z`);
  return isValid(d) ? d : null;
};

export const formatBookingDisplayTime = (
  bookingDate: string | null | undefined,
  bookingTime: string | null | undefined,
  bookingDateTime?: string | null | undefined
): string => {
  const d = parseBookingUtcInstant(bookingDate, bookingTime, bookingDateTime);
  if (!d) {
    return '';
  }
  return format(d, TIME_ONLY_FORMAT);
};

export const formatBookingDisplayDate = (
  bookingDate: string | null | undefined,
  bookingTime: string | null | undefined,
  bookingDateTime: string | null | undefined,
  formatType: FormatType
): string => {
  const d = parseBookingUtcInstant(bookingDate, bookingTime, bookingDateTime);
  if (!d) {
    return '';
  }
  const pattern = formatMap[formatType];
  return format(d, pattern);
};

const CLEANER_STYLE_DATE_FORMAT = 'MMM d, yyyy';
const CLEANER_STYLE_TIME_FORMAT = 'h:mma';
const CUSTOMER_STYLE_DATE_WITH_WEEKDAY_FORMAT = 'EEEE MMM d, yyyy';

export const formatBookingDisplayDateByState = (
  bookingDate: string | null | undefined,
  bookingTime: string | null | undefined,
  bookingDateTime: string | null | undefined,
  state: string | null | undefined
): string => {
  const instant = parseBookingUtcInstant(
    bookingDate,
    bookingTime,
    bookingDateTime
  );
  if (!instant) {
    return '';
  }
  const zoned = toBookingTimezoneDate(instant, getTimezoneFromState(state));
  if (!zoned) {
    return '';
  }
  return format(zoned, CLEANER_STYLE_DATE_FORMAT);
};

export const formatBookingDisplayTimeByState = (
  bookingDate: string | null | undefined,
  bookingTime: string | null | undefined,
  bookingDateTime: string | null | undefined,
  state: string | null | undefined
): string => {
  const instant = parseBookingUtcInstant(
    bookingDate,
    bookingTime,
    bookingDateTime
  );
  if (!instant) {
    return '';
  }
  const zoned = toBookingTimezoneDate(instant, getTimezoneFromState(state));
  if (!zoned) {
    return '';
  }
  return format(zoned, CLEANER_STYLE_TIME_FORMAT).toLowerCase();
};

export const formatBookingDisplayDateWithWeekdayByState = (
  bookingDate: string | null | undefined,
  bookingTime: string | null | undefined,
  bookingDateTime: string | null | undefined,
  state: string | null | undefined
): string => {
  const instant = parseBookingUtcInstant(
    bookingDate,
    bookingTime,
    bookingDateTime
  );
  if (!instant) {
    return '';
  }
  const zoned = toBookingTimezoneDate(instant, getTimezoneFromState(state));
  if (!zoned) {
    return '';
  }
  return format(zoned, CUSTOMER_STYLE_DATE_WITH_WEEKDAY_FORMAT);
};

/**
 * @deprecated For API booking times use {@link formatBookingDisplayTime}; this treats the
 * wall clock as local, which is wrong when `booking_time` is UTC.
 */
export const timeFormat = (timeString: string): string => {
  return dateFormat(`2000-01-01T${timeString}`, 'TIME_ONLY_FORMAT');
};

/**
 * Converts a date string from MM/dd/yyyy format to ISO format (yyyy-MM-dd)
 * Commonly used for form inputs and API calls
 * @param dateString - Date string in MM/dd/yyyy format
 * @returns Date string in ISO format (yyyy-MM-dd) or original string if parsing fails
 */
export const convertDateFormatToISO = (dateString: string): string => {
  try {
    const parsedDate = parse(dateString, 'MM/dd/yyyy', new Date());
    return format(parsedDate, ISO_DATE_FORMAT);
  } catch {
    return dateString; // fallback to original string
  }
};

/**
 * Converts an ISO date string to MM/dd/yyyy format for form display
 * @param isoDate - Date string in ISO format
 * @returns Date string in MM/dd/yyyy format or original string if parsing fails
 */
export const formatDateForForm = (isoDate: string): string => {
  try {
    const date = parseISO(isoDate);
    if (!isValid(date)) {
      throw new Error('Invalid date string');
    }
    return format(date, 'MM/dd/yyyy');
  } catch {
    return isoDate; // fallback
  }
};

/**
 * Validates if a string is in MM/dd/yyyy format
 * @param dateString - Date string to validate
 * @returns True if valid date in MM/dd/yyyy format
 */
export const isValidFormDateFormat = (dateString: string): boolean => {
  try {
    const parsedDate = parse(dateString, 'MM/dd/yyyy', new Date());
    return isValid(parsedDate);
  } catch {
    return false;
  }
};

/**
 * Converts a Date to an ISO-like UTC time string: HH:mm:ss.SSSZ
 * If date is undefined, returns a sensible default '09:00:00.000Z'
 */
export const toIsoTimeZ = (date?: Date): string => {
  if (!date) return '09:00:00.000Z';
  const iso = date.toISOString();
  return `${iso.slice(11, 23)}Z`;
};

/**
 * Convert local wall-clock time in a timezone to UTC ISO 8601 string.
 * Uses iterative Intl.DateTimeFormat adjustment for DST-safe conversion.
 */
export const convertLocalTimeToUTC = (
  dateStr: string,
  timeStr: string,
  timezone: string
): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);

  const utcDateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00Z`;
  let currentUtcDate = new Date(utcDateStr);

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  for (let i = 0; i < 10; i++) {
    const parts = formatter.formatToParts(currentUtcDate);
    const tzYear = parseInt(parts.find(p => p.type === 'year')?.value || '0');
    const tzMonth = parseInt(parts.find(p => p.type === 'month')?.value || '1');
    const tzDay = parseInt(parts.find(p => p.type === 'day')?.value || '1');
    const tzHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
    const tzMinute = parseInt(
      parts.find(p => p.type === 'minute')?.value || '0'
    );

    if (
      tzYear === year &&
      tzMonth === month &&
      tzDay === day &&
      tzHour === hours &&
      tzMinute === minutes
    ) {
      return currentUtcDate.toISOString();
    }

    let adjustmentMs =
      (hours - tzHour) * 3600000 + (minutes - tzMinute) * 60000;

    const dayDiff = day - tzDay;
    const monthDiff = month - tzMonth;
    const yearDiff = year - tzYear;

    if (dayDiff !== 0 || monthDiff !== 0 || yearDiff !== 0) {
      const targetDate = new Date(year, month - 1, day);
      const refDateLocal = new Date(tzYear, tzMonth - 1, tzDay);
      const daysDiff = Math.round(
        (targetDate.getTime() - refDateLocal.getTime()) / 86400000
      );
      adjustmentMs += daysDiff * 86400000;
    }

    currentUtcDate = new Date(currentUtcDate.getTime() + adjustmentMs);
  }

  return currentUtcDate.toISOString();
};

/** Alias for parseBookingUtcInstant — resolves booking fields to a Date, preferring booking_date_time (UTC ISO). */
export const getBookingDateTime = parseBookingUtcInstant;

/** Calendar date (YYYY-MM-DD) for a UTC instant in the given timezone. */
export const formatUtcAsDateKeyInTimezone = (
  utcDate: Date,
  timezone: string
): string => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(utcDate);
};

/** 24h HH:mm wall clock for a UTC instant in the given timezone. */
export const formatUtcAsTime24InTimezone = (
  utcDate: Date,
  timezone: string
): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(utcDate);
  const hh = parts.find(p => p.type === 'hour')?.value ?? '00';
  const mm = parts.find(p => p.type === 'minute')?.value ?? '00';
  return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`;
};

/** Distinct calendar dates in `timezone` that intersect [utcStart, utcEnd) (half-open). */
export const collectZonedDateKeysIntersectingUtcRange = (
  utcStart: Date,
  utcEnd: Date,
  timezone: string
): string[] => {
  const keys = new Set<string>();
  const startMs = utcStart.getTime();
  const endMs = utcEnd.getTime();
  if (!(startMs < endMs)) return [];
  keys.add(formatUtcAsDateKeyInTimezone(new Date(startMs), timezone));
  keys.add(formatUtcAsDateKeyInTimezone(new Date(endMs - 1), timezone));
  for (let t = startMs; t < endMs; t += 3600000) {
    keys.add(formatUtcAsDateKeyInTimezone(new Date(t), timezone));
  }
  return Array.from(keys);
};
